import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {TITLE_SUFFIX} from '../../config/constants';
import {GSSKT_HOST} from '../../config/constants/hosts';
import type {ColumnListItemType} from '../../core/entities/columns/schema';
import {
  RANKING_METRICS,
  type RankingMetric,
  getRankedColumnList,
} from '../../core/repositories/columnRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../core/repositories/usersMeRepository';
import {validateCookie} from '../../core/usecases/auth/checkOauthTokenExistence';
import {ColumnRankingPage} from '../../features/columns/components/List/RankingPage';
import {RANKING_METRIC_MAP} from '../../features/columns/components/List/constants';
import {withHandler} from '../../server/token/withHandler';
import {ScriptForGA} from '../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../shared/components/layouts/NoLeftSidebarLayout';
import type {NextPageWithLayout} from '../_app';

export type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  metric: RankingMetric;
  columns: ColumnListItemType[];
};

const isMetric = (by: string): by is RankingMetric => {
  if (RANKING_METRICS.includes(by as RankingMetric)) {
    return true;
  }
  return false;
};

const LIMIT = 30;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession'
>(
  {auth: 'optional'},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {by} = context.query;
    if (typeof by !== 'string' || !isMetric(by)) {
      throw new Error(
        `Invalid ranking metric: ${by}. Must be one of ${RANKING_METRICS.join(', ')}.`,
      );
    }
    const _columns = await getRankedColumnList({
      section: by,
      limit: LIMIT,
    });
    if (!_columns.ok) {
      throw new Error(
        `Failed to fetch columns for metric: ${by}. Error: ${_columns.error}`,
      );
    }
    const columns = _columns.value.posts;

    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);
    if (!isValid) {
      return {
        props: {
          metric: by,
          columns,
        },
      };
    }

    const userDataResponse = await getUserDataForAnalytics({cookies});
    return {
      props: {
        metric: by,
        columns,
        userDataForAnalytics: userDataResponse.ok
          ? userDataResponse.value
          : undefined,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  metric,
  columns,
}: Props) => {
  const router = useRouter();
  const canonicalURL = `${GSSKT_HOST}${decodeURI(router.asPath)}`;

  const title = `コラム（${RANKING_METRIC_MAP[metric]}ランキング）${TITLE_SUFFIX}`;
  const description =
    '就活に役立つノウハウを幅広く提供。就活、選考対策のブラッシュアップにつなげよう！';
  return (
    <>
      <Head>
        <title>{title}</title>
        <meta key="description" name="description" content={description} />
        <meta
          key="keywords"
          name="keywords"
          content={
            'コラム,就活の情報収集,外資,就活,インターンシップ,新卒採用,会社説明会'
          }
        />
        <link ref="canonical" href={canonicalURL} />
        <meta key="twitter:title" name="twitter:title" content={title} />
        <meta key="og:title" property="og:title" content={title} />
        <meta key="og:url" property="og:url" content={canonicalURL} />
      </Head>
      <ColumnRankingPage metric={metric} columns={columns} />
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;
Page.getLayout = (page) => <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;
